/**
 * The Candidate Requests queue, joined onto the scheduling cards by Lever opportunity.
 *
 * ⚠️ **It reads the queue the Requests tab already loaded** (`requestsCache`) — it never fetches.
 * Two consequences, both deliberate, and the first is a real limit rather than a bug:
 *
 *  · **No chips until 📥 Requests has been opened this session.** The cache is filled by that tab.
 *    The board payload is built server-side with no Gmail access at all, so the only other way to
 *    know a candidate has written back would be to give the board route a mailbox token — which
 *    is a much bigger boundary to move than a chip is worth (CLAUDE.md invariant #1).
 *  · **Never a colleague's mail.** The cache is owner-checked, so viewing someone else's board
 *    shows no chips rather than yours against their candidates.
 *
 * Read at mount, with no subscription: the scheduling tabs unmount on every board-tab switch, so
 * coming back from 📥 Requests re-reads a freshly filled cache — which is exactly the trip that
 * fills it. Adding a subscription would buy only the case where mail arrives while the recruiter
 * is already looking at the Scheduling tab, and the queue does not refetch there either.
 */
import { useMemo } from 'react';
import type { RoleBoard, TrackingMap } from '../../types/taActionBoard';
import { readRequestsCache } from './requestsCache';
import { indexRepliesByCandidate, type CandidateReply } from './candidateReplies';
import { proposedInTracking } from './bookableOffers';

/**
 * @param myEmail the SIGNED-IN recruiter — whose mailbox the cache belongs to, never the board
 *        being viewed.
 */
export function useCandidateReplies(
  myEmail: string,
  roles: RoleBoard[],
  tracking: TrackingMap
): Map<string, CandidateReply> {
  return useMemo(() => {
    const items = readRequestsCache(myEmail);
    if (!items?.length) return new Map<string, CandidateReply>();

    // What we proposed, per candidate, in the order we proposed it — the keycap markers in our
    // own email count from this, so the order is load-bearing rather than cosmetic.
    const offered = new Map<string, string[]>();
    for (const role of roles) {
      for (const c of role.scheduling?.candidates || []) {
        const starts = proposedInTracking(tracking[c.opportunityId]).map((s) => s.start);
        if (starts.length) offered.set(c.opportunityId, starts);
      }
    }
    return indexRepliesByCandidate(items, offered);
  }, [myEmail, roles, tracking]);
}
